import React, { useState, useEffect } from 'react';
import './SeatLayoutVisualizer.css';

interface Seat {
  id: string;
  number: number;
  row: string;
  category: string;
  price: number;
  isBooked: boolean;
  isAisle: boolean;
  isSelected: boolean;
  isEmpty?: boolean;
  displayNumber?: string;
  areaCode?: string;
  areaName?: string;
}

interface SeatRow {
  row: string;
  seats: Seat[];
  gridRowId?: number;
  areaCode?: string;
  areaName?: string;
  seatY?: number;
}

interface SeatLayoutVisualizerProps {
  seatLayout: SeatRow[]; // Changed from SeatLayout to array of rows (same as website)
  selectedSeats: string[];
  onSeatSelect: (seatIds: string[]) => void;
  disabled?: boolean; // Disable seat selection if quantity not selected
  quantity?: number;
  onError?: (message: string) => void;
}

const SeatLayoutVisualizer: React.FC<SeatLayoutVisualizerProps> = ({
  seatLayout,
  selectedSeats,
  onSeatSelect,
  disabled = false,
  quantity,
  onError,
}) => {
  const [seats, setSeats] = useState<SeatRow[]>([]);
  const [internalError, setInternalError] = useState<string | null>(null);

const showError = (message: string) => {
  if (onError) {
    onError(message);
  } else {
    setInternalError(message);
    setTimeout(() => setInternalError(null), 3000);
  }
};

  useEffect(() => {
    // Safety check: ensure seatLayout is an array
    if (!seatLayout || !Array.isArray(seatLayout)) {
      console.warn('Invalid seat layout data:', seatLayout);
      setSeats([]);
      return;
    }

    

    // Update seats with selected state
    const updatedSeats = seatLayout.map((row) => ({
      ...row,
      seats: row.seats.map((seat) => ({
        ...seat,
        isSelected: selectedSeats.includes(seat.id),
      })),
    }));

    setSeats(updatedSeats);
  }, [seatLayout, selectedSeats]);

  useEffect(() => {
    if (seats.length > 0) {
      console.log("Row order from backend:", seats.map(r => r.row));
    }
  }, [seats]);

  const getCurrentlySelected = (rows: SeatRow[]): Seat[] =>
    rows.flatMap((row) => row.seats.filter((s) => s.isSelected && !s.isEmpty));
  
  const availableUnselectedInRow = (
    rows: SeatRow[],
    rowId: string,
    areaCode: string | undefined,
  ): number =>
    rows
      .flatMap((r) => r.seats)
      .filter(
        (s) =>
          !s.isBooked &&
          !s.isEmpty &&
          !s.isSelected &&
          s.row === rowId &&
          s.areaCode === areaCode,
      ).length;
  

  // const handleSeatClick = (seat: Seat) => {
  //   if (seat.isBooked || seat.isEmpty || disabled) return;

  //   const newSelectedSeats = seat.isSelected
  //     ? selectedSeats.filter(id => id !== seat.id)
  //     : [...selectedSeats, seat.id];

  //   onSeatSelect(newSelectedSeats);
  // };

  const handleSeatClick = (clickedSeat: Seat) => {
    if (clickedSeat.isBooked || clickedSeat.isEmpty || disabled) return;
  
    const currentlySelected = getCurrentlySelected(seats);
  
    // Quantity cap
    if (quantity && !clickedSeat.isSelected && currentlySelected.length >= quantity) {
      showError(`You can only select exactly ${quantity} seat(s). Deselect a seat first.`);
      return;
    }
  
    // Same-area restriction
    if (currentlySelected.length > 0 && !clickedSeat.isSelected) {
      const firstSelected = currentlySelected[0];
      if (clickedSeat.areaCode !== firstSelected.areaCode) {
        showError('All seats must be selected from the same area.');
        return;
      }
    }
  
    // Same-row lock
    if (quantity && currentlySelected.length > 0 && !clickedSeat.isSelected) {
      const lastSelected = currentlySelected[currentlySelected.length - 1];
      const activeRow = lastSelected.row;
      const remaining = quantity - currentlySelected.length;
  
      const freeInActiveRow = availableUnselectedInRow(
        seats,
        activeRow,
        lastSelected.areaCode,
      );
  
      if (freeInActiveRow >= remaining && clickedSeat.row !== activeRow) {
        showError(`Please complete your remaining seat(s) in row ${activeRow}.`);
        return;
      }
    }
  
    // Toggle selection
    const newSelectedSeats = clickedSeat.isSelected
      ? selectedSeats.filter((id) => id !== clickedSeat.id)
      : [...selectedSeats, clickedSeat.id];
  
    onSeatSelect(newSelectedSeats);
  };

  const getSeatClass = (seat: Seat): string => {
    if (seat.isEmpty) return 'seat-empty-space';
    
    let classes = 'seat';
    if (seat.category) {
      classes += ` ${seat.category}`;
    }
    if (seat.isSelected) {
      classes += ' selected';
    }
    if (seat.isBooked) {
      classes += ' booked';
    }
    return classes;
  };

  // Group rows by area code (same as website)
  const groupRowsByArea = (rows: SeatRow[]) => {
    const rowsByArea = new Map<string, { areaCode: string; areaName: string; price: number; rows: SeatRow[] }>();
    
    rows.forEach((row) => {
      // Find the area code from the first non-empty seat in this row
      const firstNonEmptySeat = row.seats.find(s => !s.isEmpty && s.areaCode);
      if (firstNonEmptySeat && firstNonEmptySeat.areaCode) {
        const areaCode = firstNonEmptySeat.areaCode;
        const areaName = firstNonEmptySeat.areaName || 'Area';
        const areaPrice = firstNonEmptySeat.price || 0;
        
        if (!rowsByArea.has(areaCode)) {
          rowsByArea.set(areaCode, {
            areaCode: areaCode,
            areaName: areaName,
            price: areaPrice,
            rows: [],
          });
        }
        
        rowsByArea.get(areaCode)!.rows.push(row);
      }
    });
    
    // Convert to array and sort by area code for consistent display
    return Array.from(rowsByArea.values());


  };

  const areasArray = groupRowsByArea(seats);

  return (
    <div className="seat-layout-visualizer">
      {internalError && (
    <div style={{
      background: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: 6,
      padding: '8px 14px',
      marginBottom: 10,
      fontSize: 13,
      color: '#856404',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span>{internalError}</span>
      <button
        onClick={() => setInternalError(null)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
      >
        ✕
      </button>
    </div>
  )}
      

      <div className="seats-grid">
  {areasArray.map((areaData) => {

const getRowY = (row: SeatRow) => {
  if (!row.seats?.length) return null;
  const ys = row.seats.map(s => (s as any).seatY).filter(v => v != null);
  if (!ys.length) return null;
  return ys.reduce((a, b) => a + b, 0) / ys.length;
};

const sortedRows = [...areaData.rows].sort((a, b) => {
  const yA = getRowY(a);
  const yB = getRowY(b);
  
  // Use seatY only if valid AND different (higher Y = lower position)
  if (yA != null && yB != null && yA !== yB) {
    return yB - yA; // Descending: higher Y values come first (lower position)
  }
  
  // Fallback to gridRowId for your broken theatre
  if (a.gridRowId != null && b.gridRowId != null) {
    return a.gridRowId - b.gridRowId;
  }
  
  // Final fallback to alphabetical
  return (a.row || "").localeCompare(b.row || "", undefined, {
    numeric: true,
    sensitivity: "base",
  });
})

    
      // .reverse(); // ✅ chain properly (NO semicolon before reverse)

    return (
      <React.Fragment key={areaData.areaCode}>
        {/* Area Header */}
        <div className="area-header">
          <div className="area-name-price">
            <span className="area-price">₹{areaData.price}</span>
            <span className="area-name">
              {areaData.areaName.toUpperCase()}
            </span>
          </div>
          <div className="area-separator"></div>
        </div>

        {sortedRows.map((row) => (
          <div key={row.row} className="seat-row">
            <div className="row-label">{row.row}</div>

            <div className="seats-container">
              {row.seats.map((seat) => (  // // seat direction fixed - removed extra reverse to match frontend
                <React.Fragment key={seat.id}>
                  {seat.isEmpty ? (
                    <div className="seat-empty-space"></div>
                  ) : (
                    <button
                      type="button"
                      className={getSeatClass(seat)}
                      onClick={() => handleSeatClick(seat)}
                      disabled={seat.isBooked || disabled}
                    >
                      {seat.number}
                    </button>
                  )}
                  {seat.isAisle && <div className="aisle-space"></div>}
                </React.Fragment>
              ))}
            </div>

            <div className="row-label">{row.row}</div>
          </div>
        ))}
      </React.Fragment>
    );
  })}

  {/* SCREEN AT BOTTOM */}
  <div className="screen-display">
    <div className="screen-label">SCREEN</div>
  </div>
</div>



      <div className="seat-legend">
        <div className="legend-item">
          <div className="legend-seat available"></div>
          <span>Available</span>
        </div>
        <div className="legend-item">
          <div className="legend-seat selected"></div>
          <span>Selected</span>
        </div>
        <div className="legend-item">
          <div className="legend-seat booked"></div>
          <span>Booked</span>
        </div>
        <div className="legend-item">
          <div className="legend-seat premium"></div>
          <span>Premium</span>
        </div>
      </div>

      {selectedSeats.length > 0 && (
        <div className="selected-seats-info">
          <strong>Selected Seats:</strong> {selectedSeats.join(', ')} ({selectedSeats.length} seat{selectedSeats.length !== 1 ? 's' : ''})
        </div>
      )}
    </div>
  );
};

export default SeatLayoutVisualizer;
